import { format } from "date-fns";
import { Dispatch, SetStateAction, useCallback } from "react";
import toast from "react-hot-toast";

import { ItemType, UserEvent, Video } from "@/api";
import { Drawer } from "@/components";
import {
  DRAWER_CALENDAR,
  DRAWER_SCHEDULE,
  ITEM_TYPE_COLLECTION,
} from "@/const";
import { useAppCTAModal, useDeleteEvent } from "@/hooks";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { cn, itemRequiresApp } from "@/utils";

import { useCalendarData } from "../hooks/useCalendarData";
import { CalendarDrawerItem } from "./CalendarDrawerItem";
import { JournalInput } from "./JournalInput";

const Divider = ({ className }: { className?: string }) => (
  <div className={cn("mb-7 w-full border-b border-border", className)} />
);

interface CalendarDayDrawerProps {
  selectedDate: Date;
  setScheduleVideo: Dispatch<SetStateAction<Video | undefined>>;
}

export const CalendarDayDrawer = ({
  selectedDate,
  setScheduleVideo,
}: CalendarDayDrawerProps) => {
  const { openDrawer, setOpenDrawer } = useResponsiveness();
  const { openAppCTAModal } = useAppCTAModal();
  const { translate, formatDate } = useTranslate();

  const {
    getEventsForDate,
    getEventTitle,
    getEventIcon,
    getEventThumbnail,
    getEventTime,
    journalPrompts,
    collectionById,
    videoById,
  } = useCalendarData();
  const deleteEvent = useDeleteEvent();

  const eventsForSelectedDate = getEventsForDate(selectedDate) ?? [];

  const userEvents = eventsForSelectedDate?.filter((e) => !e.isClientScheduled);
  const clientEvents = eventsForSelectedDate?.filter(
    (e) => e.isClientScheduled,
  );

  const hasJournal = journalPrompts.length > 0;

  const handleDelete = useCallback(
    (e: UserEvent) => {
      deleteEvent.mutate(
        {
          ItemId: parseInt(e.ItemId),
          DataSource: e.DataSource,
          ItemType: e.ItemType,
          EventType: e.EventType,
          EventDate: e.EventDate,
        },
        {
          onSuccess: () => {
            toast(translate("Successfully deleted"));
          },
        },
      );
    },
    [deleteEvent, translate],
  );

  const getClickHandler = useCallback(
    (itemType: ItemType, itemId: string) => {
      if (!collectionById || itemType !== ITEM_TYPE_COLLECTION) {
        return undefined;
      }

      const fullCollection = collectionById[parseInt(itemId)];
      if (fullCollection && itemRequiresApp(fullCollection)) {
        return () => openAppCTAModal(fullCollection);
      }

      return undefined;
    },
    [collectionById, openAppCTAModal],
  );

  return (
    <Drawer
      title={formatDate(selectedDate)}
      open={openDrawer === DRAWER_CALENDAR}
      onOpenChange={(open) => setOpenDrawer(open ? DRAWER_CALENDAR : "")}
      className={hasJournal ? "px-[18px]" : ""}
    >
      {userEvents.map((e, idx) => {
        const handleClick = getClickHandler(e.ItemType, e.ItemId);
        // Currently the only schedulable collections are circuits, which are unsupported on
        // web but can be displayed here if the user has scheduled them in the mobile app
        const blockEditing = e.ItemType === ITEM_TYPE_COLLECTION;

        const handleScheduleAgain = () => {
          setScheduleVideo(
            videoById ? videoById[parseInt(e.ItemId)] : undefined,
          );
          setOpenDrawer(DRAWER_SCHEDULE);
        };

        return (
          <CalendarDrawerItem
            key={e.EventDate + e.EventType + e.ItemId}
            event={e}
            index={idx}
            thumbnail={getEventThumbnail(e)}
            icon={getEventIcon(e, { size: "large" })}
            title={getEventTitle(e)}
            time={getEventTime(e)}
            onScheduleAgain={blockEditing ? undefined : handleScheduleAgain}
            onDelete={
              blockEditing
                ? undefined
                : () => {
                    handleDelete(e);
                  }
            }
            handleClick={handleClick}
            isCompactView={hasJournal}
          />
        );
      })}
      {clientEvents?.length > 0 && userEvents?.length > 0 && (
        <Divider className={!hasJournal ? "mb-10" : ""} />
      )}
      {clientEvents.map((e, idx) => {
        const onClick = getClickHandler(e.ItemType, e.ItemId);

        return (
          <CalendarDrawerItem
            key={e.EventDate + e.EventType + e.ItemId}
            event={e}
            index={idx}
            thumbnail={getEventThumbnail(e)}
            icon={getEventIcon(e, { size: "large" })}
            title={getEventTitle(e)}
            time={translate("Scheduled For You")}
            handleClick={onClick}
            isCompactView={hasJournal}
          />
        );
      })}
      {!hasJournal && eventsForSelectedDate.length === 0 && (
        <div className="p2-medium flex h-full w-full items-center justify-center pb-[76px] text-item-secondary">
          {translate("You have nothing scheduled")}
        </div>
      )}
      {hasJournal && eventsForSelectedDate.length > 0 && <Divider />}
      {journalPrompts.map(([id, prompt]) => (
        <JournalInput
          key={`${id}-${format(selectedDate, "yyyy-MM-dd")}`}
          id={id}
          prompt={prompt}
          date={selectedDate}
        />
      ))}
    </Drawer>
  );
};
